import React, { useEffect, useState } from "react";

import { Handle } from "onfido-sdk-ui/types/Onfido";
import { withStringValues } from "../applications/gravity-form/components/with-inputs";
import { Applicant, OnFidoSDKProps } from "./types";
import { initOnFidoClient, createApplicant } from "./services";
import {
    JourneyCompleted,
    JourneyError,
    JourneyLoading
} from "./screens";
import { FormContext } from "../forms";
import styled, { useTheme } from "styled-components";
import { ThemeTemplate } from "../theme/theme-template";
import { media } from "../theme/style-utils-coras-nua";

export const ONFIDO_SDK_CONTAINER_ID = "onfido-sdk-container";

const OnFidoSDKContainer = styled.div`
    margin-bottom: 4.25rem;
    & > #onfido-sdk{
        ${media.sm.up`
            min-height: 710px;
        `}
    }
    @media screen and (max-width: 567px) {
        position: fixed;
        width: 100%;
        top: 0;
        left: 0;
    }
`;

const initSDK = async (
    { value, inputs, firstName, lastName, processId }: OnFidoSDKProps,
    theme: ThemeTemplate,
    setOnFidoClient: (onFidoClient: Handle) => void,
    setLoading: (loading: boolean) => void,
    setHasClientError: (hasError: boolean) => void,
    completeJourney: (applicant?: Applicant) => void
) => {
    let applicant: Applicant;
    if (value && inputs && Object.keys(value).length > 1) {
        applicant = {
            applicantId: value[inputs[0].id],
            sdkToken: value['sdkToken'],
            workflowRunId: value[inputs[2].id],
            processId: value[inputs[1].id],
            selectedDocument: value[inputs[3].id],
            attemptCount: value['attemptCount']
        };
    } else {
        applicant = await createApplicant(firstName, lastName, processId);
    }

    if (value && value['jumpToPage']) {
        applicant.jumpToPage = value['jumpToPage'];
    }

    if (!applicant.selectedDocument) {
        await initOnFidoClient(applicant, theme, setOnFidoClient, setHasClientError, completeJourney);
    } else {
        completeJourney();
    }
    setLoading(false);
};

const updateContextValue = (
    applicant: Applicant,
    name?: string,
    inputs?: { id: string; label: string, name: string }[],
    context?: FormContext
) => {
    name && inputs && context?.setValue(name, {
        'sdkToken': applicant.sdkToken,
        'attemptCount': applicant.attemptCount,
        'jumpToPage': applicant.jumpToPage,
        [inputs[0].id]: applicant.applicantId,
        [inputs[1].id]: applicant.processId,
        [inputs[2].id]: applicant.workflowRunId,
        [inputs[3].id]: applicant.selectedDocument
    });
};

const OnFidoSDK = (props: OnFidoSDKProps) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [completed, setCompleted] = useState<boolean>(false);
    const [hasClientError, setHasClientError] = useState<boolean>(false);
    const [onFidoClient, setOnFidoClient] = useState<Handle|undefined>(undefined);
    const theme = useTheme() as ThemeTemplate;

    useEffect(() => {
        initSDK(props, theme, setOnFidoClient, setLoading, setHasClientError, completeJourney);
        
        return () => {
            onFidoClient && onFidoClient.tearDown();
        };
    }, []);

    const completeJourney = (applicant?: Applicant) => {
        applicant && updateContextValue(applicant, props.name, props.inputs, props.context);
        setCompleted(true);
    };

    return (
        <>
            {loading && <JourneyLoading />} 
            {hasClientError
                ? <JourneyError />
                : completed
                ? <JourneyCompleted context={props.context} value={props.value} />
                : <OnFidoSDKContainer id={ONFIDO_SDK_CONTAINER_ID} />
            }
        </>
    );
}

export default withStringValues(OnFidoSDK, [
    "firstName",
    "lastName"
]);
